<template>
  <div class="group-preview">
    <div v-if="!hideName && (group.name || group.points)" class="group-preview__head">
      <h3 v-if="group.name" class="group-preview__name">{{ group.name }}</h3>
      <span v-if="group.points" class="group-preview__points">{{ group.points }} pts</span>
    </div>

    <div v-if="group.abilities.length" class="group-preview__abilities">
      <AbilityCard
        v-for="a in group.abilities"
        :key="a.id"
        :ability="a"
        :points="showPoints ? group.points : undefined"
      />
    </div>

    <div v-for="(rule, i) in group.rules" :key="'rule-' + i" class="group-preview__rule">
      <div class="group-preview__rule-name">{{ rule.name }}</div>
      <div class="group-preview__rule-desc">{{ rule.description }}</div>
    </div>

    <p v-if="!group.abilities.length && !group.rules.length" class="group-preview__empty">
      Nothing to preview yet.
    </p>
  </div>
</template>

<script setup lang="ts">
import type { IUpgrade } from '@scrolldex/shared';
import AbilityCard from '../AbilityCard.vue';

// hideName: omit the group's own name (the parent already labels it — e.g. the
// Battle Traits section heading is the same as the group name).
// showPoints: render the upgrade's point cost on its ability card (enhancements
// carry a cost; lores/traits don't show one here).
defineProps<{ group: IUpgrade; hideName?: boolean; showPoints?: boolean }>();
</script>

<style scoped>
.group-preview {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-3);
}
.group-preview__head {
  display: flex;
  align-items: baseline;
  gap: var(--st-space-2);
}
.group-preview__name {
  font-size: var(--st-text-lg);
  font-weight: var(--st-weight-bold);
}
.group-preview__points {
  padding: 1px var(--st-space-2);
  border-radius: var(--st-radius-full);
  background: var(--badge-red);
  color: var(--badge-red-text);
  font-size: var(--st-text-xs);
  font-weight: var(--st-weight-bold);
}
.group-preview__abilities {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-2);
}
.group-preview__rule-name {
  font-weight: var(--st-weight-semibold);
  font-size: var(--st-text-sm);
}
.group-preview__rule-desc {
  font-size: var(--st-text-sm);
  color: var(--st-text-muted);
  white-space: pre-wrap;
}
.group-preview__empty {
  color: var(--st-text-muted);
  font-size: var(--st-text-sm);
}
</style>
